import { OrderFilters, PaymentModeFilters } from '../models/order.models';
import type { Order } from '../models/order.models';
import type { FilterCategory } from '../../shared/components/filter-modal/filter-modal.component';
import type { PaginationData } from '../../shared/components/pagination-bar/pagination-bar.component';
import { takeUntil, type Observable } from 'rxjs';
import type { LocationOption } from '../../shared/types';
import {
  createDefaultPaymentModeFilters,
  syncPaymentModeFilterCategoryOptions,
  type PaymentModeApiFilterItem,
} from '../constants/orders.constants';

interface SourceFilters {
  wayCom: boolean;
  scanAndGo: boolean;
  wayApp: boolean;
  website: boolean;
}

type SortDirection = 'asc' | 'desc' | 'none';

/**
 * Keep API payload mapping in one place so admin/staff pages
 * stay focused on role-specific behavior.
 */
export const CANCEL_ORDER_ERROR_DETAIL = 'Failed to cancel order. Please try again.';

export function buildStatusFiltersFromOrderFilters(filters: OrderFilters) {
  return {
    completed: filters.status.completed,
    pending: filters.status.pending,
    cancelled: filters.status.canceled,
    scheduled: filters.status.scheduled,
    inProgress: filters.status.inProgress,
  };
}

export function buildWashTypeFiltersFromOrderFilters(filters: OrderFilters) {
  return {
    singleWash: filters.carWashType.singleWash,
    unlimitedWash: filters.carWashType.monthlyMembership,
    prepaidWash: filters.carWashType.prepaidWash,
    rewash: filters.carWashType.rewash,
  };
}

export function createDefaultSourceFilters(): SourceFilters {
  return {
    wayCom: false,
    scanAndGo: false,
    wayApp: false,
    website: false,
  };
}

export function buildSourceFiltersFromOrderFilters(filters: OrderFilters): SourceFilters {
  return {
    wayCom: filters.source?.wayCom || false,
    scanAndGo: filters.source?.scanAndGo || false,
    wayApp: filters.source?.wayApp || false,
    website: filters.source?.website || false,
  };
}

export function buildPaymentModeFiltersFromOrderFilters(filters: OrderFilters): PaymentModeFilters {
  const base = createDefaultPaymentModeFilters();
  const paymentMode = filters.paymentMode;
  if (!paymentMode) {
    return base;
  }
  return {
    cash: !!paymentMode.cash,
    card: !!paymentMode.card,
    link: !!paymentMode.link,
    tapToPay: !!paymentMode.tapToPay,
    terminal: !!paymentMode.terminal,
    additional: [...(paymentMode.additional ?? [])],
  };
}

/** True when the location has at least one linked Stripe Terminal reader (same rule as checkout). */
export function hasLinkedTerminalReaders(readers: any[] | null | undefined): boolean {
  return Array.isArray(readers) && readers.length > 0;
}

/** Drop Terminal Payment from applied filters when the option is hidden for the current location. */
export function clearTerminalPaymentFilterFromOrderFilters(filters: OrderFilters): OrderFilters {
  if (!filters.paymentMode?.terminal) {
    return filters;
  }
  const next = cloneOrderFilters(filters);
  if (next.paymentMode) {
    next.paymentMode.terminal = false;
  }
  return next;
}

/** Rebuild Payment Mode category options (built-in + API + optional Terminal). */
export function syncOrderPaymentModeFilterCategories(
  categories: FilterCategory[],
  apiModes: PaymentModeApiFilterItem[],
  selected: PaymentModeFilters | undefined,
  showTerminalPayment: boolean
): FilterCategory[] {
  return syncPaymentModeFilterCategoryOptions(categories, apiModes, selected, {
    showTerminalPayment,
  });
}

/** Load GET `user/payment-modes` for the Orders Payment Mode filter. */
export function loadOrderPaymentModesForFilter(
  getPaymentModes: () => Observable<PaymentModeApiFilterItem[]>,
  destroy$: Observable<void>,
  onResult: (modes: PaymentModeApiFilterItem[]) => void
): void {
  getPaymentModes()
    .pipe(takeUntil(destroy$))
    .subscribe({
      next: onResult,
      error: () => onResult([]),
    });
}

/**
 * Clear Terminal from applied filters when the option is hidden.
 * Admin/staff also pass `currentFilters`; tunnel may omit it.
 */
export function applyOrderTerminalPaymentFilterVisibility(
  visible: boolean,
  appliedFilters: OrderFilters,
  currentFilters?: OrderFilters
): {
  showTerminalPaymentFilter: boolean;
  appliedFilters: OrderFilters;
  currentFilters?: OrderFilters;
} {
  if (visible) {
    return {
      showTerminalPaymentFilter: true,
      appliedFilters,
      ...(currentFilters !== undefined ? { currentFilters } : {}),
    };
  }

  return {
    showTerminalPaymentFilter: false,
    appliedFilters: clearTerminalPaymentFilterFromOrderFilters(appliedFilters),
    ...(currentFilters !== undefined
      ? { currentFilters: clearTerminalPaymentFilterFromOrderFilters(currentFilters) }
      : {}),
  };
}

/** Resolve linked-reader availability for the Terminal Payment filter option. */
export function loadOrderTerminalPaymentFilterAvailability(
  listReaders: (locationId: string) => Observable<any[]>,
  locationId: string,
  destroy$: Observable<void>,
  onVisible: (visible: boolean) => void
): void {
  const id = String(locationId || '').trim();
  if (!id) {
    onVisible(false);
    return;
  }

  listReaders(id)
    .pipe(takeUntil(destroy$))
    .subscribe({
      next: readers => onVisible(hasLinkedTerminalReaders(readers)),
      error: () => onVisible(false),
    });
}

function applyPaymentModeOption(
  filters: PaymentModeFilters,
  optionValue: string,
  selected: boolean
): void {
  switch (optionValue) {
    case 'cash':
      filters.cash = selected;
      break;
    case 'card':
      filters.card = selected;
      break;
    case 'link':
      filters.link = selected;
      break;
    case 'tapToPay':
      filters.tapToPay = selected;
      break;
    case 'terminal':
      filters.terminal = selected;
      break;
    default: {
      if (!selected) {
        filters.additional = filters.additional.filter(mode => mode !== optionValue);
        return;
      }
      if (!filters.additional.includes(optionValue)) {
        filters.additional.push(optionValue);
      }
      break;
    }
  }
}

export function cloneOrderFilters(filters: OrderFilters): OrderFilters {
  return {
    carWashType: { ...filters.carWashType },
    status: { ...filters.status },
    ...(filters.source ? { source: { ...filters.source } } : {}),
    paymentMode: buildPaymentModeFiltersFromOrderFilters(filters),
    packageIds: [...(filters.packageIds ?? [])],
    ...(typeof filters.discountApplied === 'string'
      ? { discountApplied: filters.discountApplied }
      : {}),
    ...(typeof filters.period === 'string' ? { period: filters.period } : {}),
  };
}

/**
 * Convert shared filter modal categories to OrderFilters.
 * Uses a clone of defaultFilters so admin/staff/orders can pass their own defaults.
 */
export function convertCategoriesToOrderFilters(
  categories: FilterCategory[],
  defaultFilters: OrderFilters
): OrderFilters {
  const filters = cloneOrderFilters(defaultFilters);

  for (const category of categories) {
    const selectedOptions = category.options.filter(option => option.selected);

    switch (category.id) {
      case 'carWashType':
        selectedOptions.forEach(option => {
          if (option.value === 'monthlyMembership') filters.carWashType.monthlyMembership = true;
          if (option.value === 'singleWash') filters.carWashType.singleWash = true;
          if (option.value === 'prepaidWash') filters.carWashType.prepaidWash = true;
          if (option.value === 'rewash') filters.carWashType.rewash = true;
        });
        break;
      case 'status':
        selectedOptions.forEach(option => {
          if (option.value === 'pending') filters.status.pending = true;
          if (option.value === 'completed') filters.status.completed = true;
          if (option.value === 'inProgress') filters.status.inProgress = true;
          if (option.value === 'scheduled') filters.status.scheduled = true;
          if (option.value === 'canceled') filters.status.canceled = true;
        });
        break;
      case 'source':
        if (filters.source) {
          selectedOptions.forEach(option => {
            if (option.value === 'wayCom') filters.source!.wayCom = true;
            if (option.value === 'scanAndGo') filters.source!.scanAndGo = true;
            if (option.value === 'wayApp') filters.source!.wayApp = true;
            if (option.value === 'website') filters.source!.website = true;
          });
        }
        break;
      case 'paymentMode':
        filters.paymentMode = createDefaultPaymentModeFilters();
        selectedOptions.forEach(option => {
          applyPaymentModeOption(filters.paymentMode!, option.value, true);
        });
        break;
      case 'package':
        filters.packageIds = selectedOptions.map(option => option.value);
        break;
      case 'discountApplied':
        filters.discountApplied =
          selectedOptions[0]?.value || defaultFilters.discountApplied || 'all';
        break;
      case 'period':
        if (selectedOptions.length > 0) {
          filters.period = selectedOptions[0].value;
        }
        break;
    }
  }

  return filters;
}

export function countStatusFilters(filters: OrderFilters): number {
  return Object.values(filters.status).filter(Boolean).length;
}

export function countActiveOrderFilters(filters: OrderFilters): number {
  let count = 0;
  count += Object.values(filters.carWashType).filter(Boolean).length;
  count += Object.values(filters.status).filter(Boolean).length;
  if (filters.source) {
    count += Object.values(filters.source).filter(Boolean).length;
  }
  if (filters.paymentMode) {
    const { additional, ...builtIn } = filters.paymentMode;
    count += Object.values(builtIn).filter(Boolean).length;
    count += additional?.length ?? 0;
  }
  count += filters.packageIds?.length ?? 0;
  if (
    filters.discountApplied &&
    filters.discountApplied !== 'all' &&
    String(filters.discountApplied).trim() !== ''
  ) {
    count += 1;
  }
  return count;
}

/** True when any filter (wash type, status, source, payment mode) is selected. */
export function hasActiveFiltersFromFilters(filters: OrderFilters): boolean {
  return countActiveOrderFilters(filters) > 0;
}

/** True when any status filter is selected (used for "main" filter modal state). */
export function hasMainFiltersSelectedFromFilters(filters: OrderFilters): boolean {
  return countStatusFilters(filters) > 0;
}

/** True when filters differ from defaults (main filters or any active filter). */
export function hasFilterChangesFromFilters(filters: OrderFilters): boolean {
  return hasMainFiltersSelectedFromFilters(filters) || hasActiveFiltersFromFilters(filters);
}

/**
 * Whether a filter option is currently applied in the given OrderFilters.
 * Used by order list components to sync filter modal checkbox state with applied filters.
 */
export function isOptionAppliedFromOrderFilters(
  filters: OrderFilters,
  categoryId: string,
  optionValue: string
): boolean {
  switch (categoryId) {
    case 'carWashType':
      if (optionValue === 'monthlyMembership') return filters.carWashType.monthlyMembership;
      if (optionValue === 'singleWash') return filters.carWashType.singleWash;
      if (optionValue === 'prepaidWash') return filters.carWashType.prepaidWash;
      if (optionValue === 'rewash') return filters.carWashType.rewash;
      break;
    case 'status':
      if (optionValue === 'pending') return filters.status.pending;
      if (optionValue === 'completed') return filters.status.completed;
      if (optionValue === 'inProgress') return filters.status.inProgress;
      if (optionValue === 'scheduled') return filters.status.scheduled;
      if (optionValue === 'canceled') return filters.status.canceled;
      break;
    case 'source':
      if (optionValue === 'wayCom') return filters.source?.wayCom ?? false;
      if (optionValue === 'scanAndGo') return filters.source?.scanAndGo ?? false;
      if (optionValue === 'wayApp') return filters.source?.wayApp ?? false;
      if (optionValue === 'website') return filters.source?.website ?? false;
      break;
    case 'paymentMode': {
      const paymentMode = filters.paymentMode ?? createDefaultPaymentModeFilters();
      switch (optionValue) {
        case 'cash':
          return paymentMode.cash;
        case 'card':
          return paymentMode.card;
        case 'link':
          return paymentMode.link;
        case 'tapToPay':
          return paymentMode.tapToPay;
        case 'terminal':
          return paymentMode.terminal;
        default:
          return paymentMode.additional?.includes(optionValue) ?? false;
      }
    }
    case 'package':
      return filters.packageIds?.includes(optionValue) ?? false;
    case 'discountApplied': {
      const applied = filters.discountApplied || 'all';
      return applied === optionValue;
    }
    case 'period':
      return filters.period === optionValue;
  }
  return false;
}

/**
 * Return a copy of filter categories with all option.selected set to false.
 * Use with signal update: filterCategories.update(resetFilterCategoriesToFalse).
 */
export function resetFilterCategoriesToFalse(categories: FilterCategory[]): FilterCategory[] {
  return categories.map(category => ({
    ...category,
    options: category.options.map(option => ({ ...option, selected: false })),
  }));
}

/**
 * Returns an updater for filter categories that sets each option.selected from appliedFilters.
 * Use with signal update: filterCategories.update(getResetFilterCategoriesToAppliedStateUpdater(this.appliedFilters)).
 */
export function getResetFilterCategoriesToAppliedStateUpdater(
  appliedFilters: OrderFilters
): (categories: FilterCategory[]) => FilterCategory[] {
  return (categories: FilterCategory[]) =>
    categories.map(category => ({
      ...category,
      options: category.options.map(option => ({
        ...option,
        selected: isOptionAppliedFromOrderFilters(appliedFilters, category.id, option.value),
      })),
    }));
}

export function createPaymentEventKey(eventName: string, data: any): string {
  return (
    data?.payment_intent_id ||
    data?.payment_intent ||
    data?.order_id ||
    data?.orderId ||
    `${eventName}-${Date.now()}`
  );
}

/**
 * Checks whether a clicked element is inside a date picker or related dropdown element.
 * @example
 * isInsideDatePickerClick(document.querySelector('.p-datepicker')) // true
 * @param {HTMLElement} target - The event target element to check.
 * @returns {boolean} True if the target is inside a date picker or related dropdown, otherwise false.
 */
export function isInsideDatePickerClick(target: HTMLElement): boolean {
  return Boolean(
    target.closest('.period-dropdown-wrapper') ||
    target.closest('.dropdown-menu') ||
    target.closest('.p-datepicker') ||
    target.closest('.p-calendar') ||
    target.closest('.p-datepicker-panel') ||
    target.closest('.p-datepicker-header') ||
    target.closest('.p-datepicker-month') ||
    target.closest('.p-datepicker-year') ||
    target.closest('.p-datepicker-prev') ||
    target.closest('.p-datepicker-next') ||
    target.closest('.p-datepicker-prev-icon') ||
    target.closest('.p-datepicker-next-icon')
  );
}

/**
 * Compute the next sorting state (column and direction) when toggling or selecting a sort column.
 * @example
 * getNextSortState('name', 'asc', 'name')
 * { sortColumn: 'name', sortDirection: 'desc' }
 * @param {{string|null}} {{currentColumn}} - The currently sorted column name or null if no column is sorted.
 * @param {{SortDirection}} {{currentDirection}} - The current sort direction ('asc' or 'desc').
 * @param {{string}} {{targetColumn}} - The column for which to compute the next sort state.
 * @returns {{ sortColumn: string; sortDirection: 'asc' | 'desc' }} Object with the new sortColumn and sortDirection.
 **/
export function getNextSortState(
  currentColumn: string | null,
  currentDirection: SortDirection,
  targetColumn: string
): { sortColumn: string; sortDirection: 'asc' | 'desc' } {
  if (currentColumn === targetColumn) {
    return {
      sortColumn: targetColumn,
      sortDirection: currentDirection === 'asc' ? 'desc' : 'asc',
    };
  }

  return {
    sortColumn: targetColumn,
    sortDirection: 'asc',
  };
}

interface OrdersSortService {
  sortOrdersByOrderId(orders: Order[], direction: 'asc' | 'desc' | SortDirection): Order[];
  sortOrdersByDateTime(orders: Order[], direction: 'asc' | 'desc'): Order[];
  paginateOrders(orders: Order[], page: number, limit: number): Order[];
}

/**
 * Apply sort by order ID and return updated orders and sort state. Shared by admin-orders and staff-orders.
 */
export function applySortByOrderId(
  orders: Order[],
  ordersService: OrdersSortService,
  sortColumn: string | null,
  sortDirection: SortDirection
): { orders: Order[]; sortColumn: string; sortDirection: 'asc' | 'desc' } {
  const nextState = getNextSortState(sortColumn, sortDirection, 'orderId');
  return {
    orders: ordersService.sortOrdersByOrderId(orders, nextState.sortDirection),
    sortColumn: nextState.sortColumn,
    sortDirection: nextState.sortDirection,
  };
}

/**
 * Apply sort by date/time and return updated orders and sort state. Shared by admin-orders and staff-orders.
 */
export function applySortByDateTime(
  orders: Order[],
  ordersService: OrdersSortService,
  sortColumn: string | null,
  sortDirection: SortDirection
): { orders: Order[]; sortColumn: string; sortDirection: 'asc' | 'desc' } {
  const nextState = getNextSortState(sortColumn, sortDirection, 'dateTime');
  return {
    orders: ordersService.sortOrdersByDateTime(orders, nextState.sortDirection),
    sortColumn: nextState.sortColumn,
    sortDirection: nextState.sortDirection,
  };
}

export function calculateTotalPages(totalItems: number, itemsPerPage: number): number {
  if (!totalItems || totalItems <= 0) {
    return 1;
  }
  return Math.ceil(totalItems / itemsPerPage);
}

export function isValidPageNumber(page: number, totalPages: number): boolean {
  return page >= 1 && page <= totalPages;
}

/**
 * Apply a page change if valid. Shared by admin-orders, staff-orders, orders.
 */
export function applyPageChange(
  ctx: { totalPages: number; setCurrentPage: (page: number) => void; loadOrders: () => void },
  page: number
): void {
  if (isValidPageNumber(page, ctx.totalPages)) {
    ctx.setCurrentPage(page);
    ctx.loadOrders();
  }
}

/**
 * Apply an items-per-page change and reload from first page. Shared by admin-orders, staff-orders, orders.
 */
export function applyItemsPerPageChange(
  ctx: {
    setItemsPerPage: (v: number) => void;
    setCurrentPage: (page: number) => void;
    loadOrders: () => void;
  },
  newItemsPerPage: number
): void {
  ctx.setItemsPerPage(newItemsPerPage);
  ctx.setCurrentPage(1);
  ctx.loadOrders();
}

/**
 * Build pagination data for the pagination bar. Shared by admin-orders, staff-orders, orders.
 */
export function buildPaginationData(
  currentPage: number,
  totalPages: number,
  totalItems: number,
  itemsPerPage: number
): PaginationData {
  return {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
  };
}

/**
 * Context for loading locations in order list pages (admin-orders, orders).
 */
export interface OrderListLocationContext {
  getSelectedLocationId(): string;
  setLocationOptions(opts: LocationOption[]): void;
  setSelectedLocationId(id: string): void;
  setCurrentLocation(label: string): void;
  loadOrders(): void;
}

/**
 * Load locations, set default selection if none, and optionally load orders. Shared by admin-orders and orders.
 */
export function loadLocationsForOrderList(
  locationService: {
    getLocations(): Observable<{ status: boolean; data?: any }>;
    getLocationOptions(): LocationOption[];
  },
  messageService: {
    add(msg: { severity?: string; summary?: string; detail?: string; [key: string]: any }): void;
  },
  ctx: OrderListLocationContext
): void {
  locationService.getLocations().subscribe({
    next: response => {
      if (response.status && response.data) {
        const options = locationService.getLocationOptions();
        ctx.setLocationOptions(options);
        if (options.length > 0 && !ctx.getSelectedLocationId()) {
          ctx.setSelectedLocationId(options[0].value);
          ctx.setCurrentLocation(options[0].label);
          ctx.loadOrders();
        }
      }
    },
    error: error => {
      console.error('Error loading locations:', error);
      messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load locations',
      });
    },
  });
}

/**
 * Load an order by ID and open the details modal. Shared by orders, admin-orders, staff-orders.
 * Call when a new order is created and the creator wants to view it immediately.
 */
export interface ViewOrderDetailsCallbacks {
  closeAddOrderModal(): void;
  loadOrders(): void;
  loadOrderStats(): void;
  setSelectedOrder(order: Order): void;
  showOrderDetailsModal(): void;
  addErrorMessage(): void;
}

/**
 * Create a set of callbacks for viewing order details using the provided context.
 * @example
 * buildViewOrderDetailsCallbacks(ctx)
 * { closeAddOrderModal: [Function], loadOrders: [Function], loadOrderStats: [Function], setSelectedOrder: [Function], showOrderDetailsModal: [Function], addErrorMessage: [Function] }
 * @param {{closeAddOrderModal(): void; loadOrders(): void; loadOrderStats(): void; setSelectedOrder(order: Order): void; showOrderDetailsModal(): void; messageService: { add: (msg: { severity: string; summary: string; detail: string }) => void }}} ctx - Context object providing order-related callbacks and a message service.
 * @returns {{ViewOrderDetailsCallbacks}} Object containing callbacks to control order details view and to show an error message.
 **/
export function buildViewOrderDetailsCallbacks(ctx: {
  closeAddOrderModal(): void;
  loadOrders(): void;
  loadOrderStats(): void;
  setSelectedOrder(order: Order): void;
  showOrderDetailsModal(): void;
  messageService: {
    add: (msg: {
      severity?: string;
      summary?: string;
      detail?: string;
      [key: string]: any;
    }) => void;
  };
}): ViewOrderDetailsCallbacks {
  return {
    closeAddOrderModal: () => ctx.closeAddOrderModal(),
    loadOrders: () => ctx.loadOrders(),
    loadOrderStats: () => ctx.loadOrderStats(),
    setSelectedOrder: order => ctx.setSelectedOrder(order),
    showOrderDetailsModal: () => ctx.showOrderDetailsModal(),
    addErrorMessage: () =>
      ctx.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load order details. Please try again.',
      }),
  };
}

/**
 * Open and display the details for a newly created order: closes the add-order modal, reloads orders and stats, fetches the order by ID, and opens the order details modal with the fetched order.
 * @example
 * viewOrderDetailsFromNewOrder('order123', ordersService, destroy$, callbacks)
 * undefined
 * @param {{string}} orderId - The ID of the order to fetch and display.
 * @param {{{ getOrderById(id: string): Observable<Order> }}} ordersService - Service providing getOrderById which returns an Observable<Order>.
 * @param {{Observable<void>}} destroy$ - Observable that emits on component destruction to cancel the subscription.
 * @param {{ViewOrderDetailsCallbacks}} callbacks - Callbacks used to update UI state (close modal, load lists/stats, set selected order, show details, report errors).
 * @returns {{void}} Void.
 **/
export function viewOrderDetailsFromNewOrder(
  orderId: string,
  ordersService: { getOrderById(id: string): Observable<Order> },
  destroy$: Observable<void>,
  callbacks: ViewOrderDetailsCallbacks
): void {
  callbacks.closeAddOrderModal();
  callbacks.loadOrders();
  callbacks.loadOrderStats();
  ordersService
    .getOrderById(orderId)
    .pipe(takeUntil(destroy$))
    .subscribe({
      next: (order: Order) => {
        callbacks.setSelectedOrder(order);
        callbacks.showOrderDetailsModal();
      },
      error: callbacks.addErrorMessage,
    });
}

/**
 * Position an actions dropdown relative to its trigger button.
 * Shared by the openDropdown helper in orders, admin-orders, staff-orders.
 */
export function positionActionsDropdown(event: Event): void {
  setTimeout(() => {
    const button = event.target as HTMLElement;
    const dropdown = document.querySelector('.actions-dropdown-menu') as HTMLElement;
    if (button && dropdown) {
      const rect = button.getBoundingClientRect();
      dropdown.style.top = `${rect.bottom + 5}px`;
      dropdown.style.left = `${rect.right - dropdown.offsetWidth}px`;
    }
  }, 0);
}

export interface OrderActionsDropdownState {
  openDropdownId: string | null;
  selectedOrderForDropdown: Order | null;
}

export function closeOrderActionsDropdown(state: OrderActionsDropdownState): void {
  state.openDropdownId = null;
  state.selectedOrderForDropdown = null;
}

export function openOrderActionsDropdown(
  state: OrderActionsDropdownState,
  event: Event,
  orderId: string,
  orders: Order[]
): void {
  state.openDropdownId = orderId;
  state.selectedOrderForDropdown = orders.find(order => order.id === orderId) || null;
  positionActionsDropdown(event);
}

/**
 * Toggle the visibility of the order actions dropdown for a specific order.
 * @example
 * toggleOrderActionsDropdown(state, event, 'orderId123', orders)
 * undefined
 * @param {OrderActionsDropdownState} state - Current dropdown state object.
 * @param {Event} event - DOM event that triggered the toggle; propagation is stopped.
 * @param {string} orderId - ID of the order whose dropdown should be toggled.
 * @param {Order[]} orders - List of orders used when opening the dropdown.
 * @returns {void} No return value.
 **/
export function toggleOrderActionsDropdown(
  state: OrderActionsDropdownState,
  event: Event,
  orderId: string,
  orders: Order[]
): void {
  event.stopPropagation();
  if (state.openDropdownId === orderId) {
    closeOrderActionsDropdown(state);
  } else {
    openOrderActionsDropdown(state, event, orderId, orders);
  }
}

export interface OrderDetailsModalState extends OrderActionsDropdownState {
  selectedOrder: Order | null;
  showOrderDetailsModal: boolean;
}

export function openOrderDetailsFromActions(state: OrderDetailsModalState, order: Order): void {
  closeOrderActionsDropdown(state);
  state.selectedOrder = order;
  state.showOrderDetailsModal = true;
}

/** Open order details modal (e.g. from row click). Shared by admin-orders, staff-orders, admin-dashboard. */
export function openOrderDetailsModal(state: OrderDetailsModalState, order: Order): void {
  state.selectedOrder = order;
  state.showOrderDetailsModal = true;
}

/** Close order details modal. Shared by admin-orders, staff-orders, admin-dashboard. */
export function closeOrderDetailsModal(state: OrderDetailsModalState): void {
  state.showOrderDetailsModal = false;
  state.selectedOrder = null;
}

export interface OrderCancelModalState extends OrderActionsDropdownState {
  orderToCancel: Order | null;
  showCancelOrderModal: boolean;
}

export function openCancelOrderFromActions(state: OrderCancelModalState, order: Order): void {
  closeOrderActionsDropdown(state);
  state.orderToCancel = order;
  state.showCancelOrderModal = true;
}

/**
 * Close the cancel order modal (clear state). Shared by admin-orders and staff-orders.
 */
export function closeCancelOrderModalState(state: {
  showCancelOrderModal: boolean;
  orderToCancel: any;
}): void {
  state.showCancelOrderModal = false;
  state.orderToCancel = null;
}

export interface OrdersSortToggleState {
  sortColumn: string;
  sortDirection: SortDirection;
  orders: Order[];
}

function isActiveDirection(direction: SortDirection): direction is 'asc' | 'desc' {
  return direction === 'asc' || direction === 'desc';
}

/**
 * Re-apply the active column sort to a freshly loaded page of orders.
 * Sorting is client-side over the current page only — it never triggers extra API calls.
 */
export function applyPostLoadOrderSort(
  orders: Order[],
  sortColumn: string,
  sortDirection: SortDirection,
  ordersService: Pick<OrdersSortService, 'sortOrdersByDateTime' | 'sortOrdersByOrderId'>
): Order[] {
  if (!isActiveDirection(sortDirection)) {
    return orders;
  }
  if (sortColumn === 'dateTime') {
    return ordersService.sortOrdersByDateTime(orders, sortDirection);
  }
  if (sortColumn === 'orderId') {
    return ordersService.sortOrdersByOrderId(orders, sortDirection);
  }
  return orders;
}

export function toggleOrderIdSort(
  state: OrdersSortToggleState,
  ordersService: Pick<OrdersSortService, 'sortOrdersByOrderId'>
): void {
  if (state.sortColumn === 'orderId') {
    state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
  } else {
    state.sortColumn = 'orderId';
    state.sortDirection = 'asc';
  }

  state.orders = ordersService.sortOrdersByOrderId(state.orders, state.sortDirection);
}

/** Sorts the currently loaded page in place — no reload, no extra API calls. */
export function toggleDateTimeSort(
  state: OrdersSortToggleState,
  ordersService: Pick<OrdersSortService, 'sortOrdersByDateTime'>
): void {
  if (state.sortColumn === 'dateTime') {
    state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
  } else {
    state.sortColumn = 'dateTime';
    state.sortDirection = 'asc';
  }

  state.orders = ordersService.sortOrdersByDateTime(state.orders, state.sortDirection);
}

export function getOrdersSortIcon(
  column: string,
  sortColumn: string,
  sortDirection: SortDirection
): string {
  if (sortColumn !== column) {
    return '/assets/images/tabler_arrows_sort.png';
  }

  const iconMap: Record<string, string> = {
    orderId:
      sortDirection === 'asc'
        ? '/assets/images/sort-ascending-numbers.svg'
        : '/assets/images/sort-descending-numbers.svg',
    dateTime:
      sortDirection === 'asc'
        ? '/assets/images/time_sort_ascending.png'
        : '/assets/images/time_sort_descending.png',
  };

  return iconMap[column] || '/assets/images/tabler_arrows_sort.png';
}

/**
 * Run cancel order API and on success update cancellation modal state, reload orders and stats.
 * Shared by admin-dashboard and staff-orders onCancelOrderConfirm.
 */
export function runCancelOrderConfirm(
  data: { order: Order; reason: string },
  cancellationService: { cancelOrder(orderId: string, reason: string): Observable<any> },
  messageService: { add(msg: any): void },
  callbacks: {
    onSuccess: (wasScheduled: boolean, refundAmount: string) => void;
    loadOrders: () => void;
    loadOrderStats: () => void;
    closeModal: () => void;
  }
): void {
  cancellationService.cancelOrder(data.order.id, data.reason).subscribe({
    next: response => {
      if (response.status) {
        const wasScheduledOrder = data.order?.status?.value === 'scheduled';
        const orderPrice = data.order?.price || '';
        callbacks.onSuccess(wasScheduledOrder, orderPrice);
        callbacks.loadOrders();
        callbacks.loadOrderStats();
      } else {
        messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: CANCEL_ORDER_ERROR_DETAIL,
        });
      }
      callbacks.closeModal();
    },
    error: error => {
      console.error('Error cancelling order:', error);
      const detail = error?.error?.message || error?.message || CANCEL_ORDER_ERROR_DETAIL;
      messageService.add({ severity: 'error', summary: 'Error', detail });
      callbacks.closeModal();
    },
  });
}
